import React, { useEffect, useState } from "react";
import { Control, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { AlertCircle, Check, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import CustomerUser, { NewCustomerUser } from "../../../models/CustomerUser";
import AppInputNew from "../../Core/AppInput/AppInputNew";
import { FormLabel } from "../../Core/AppText/AppText";
import OutlineButton from "../../Core/OutlineButton";

const emptyContactPerson: CustomerUser = {
	id: "",
	firstName: "",
	lastName: "",
	email: "",
	profile: { phoneNumber: "" },
};

interface ContactPersonProps {
	value?: CustomerUser | NewCustomerUser;
	onChange?: (user: CustomerUser | NewCustomerUser) => void;
	errors?: any;
	customerUsers: CustomerUser[];
	className?: string;
}

/**
 * The job form's contact person block: free-text person fields plus a
 * "Välj kontaktperson" dropdown that prefills them from the customer's
 * existing users. Editing after picking is allowed — the value is always the
 * (possibly edited) field contents, not a user reference.
 */
const ContactPerson: React.FC<ContactPersonProps> = ({
	value,
	onChange,
	errors,
	customerUsers,
	className,
}) => {
	const { t } = useTranslation();

	// Array-level error (no contact person at all) vs per-field errors on the
	// first (only) entry. The former can't attach to a single input, so it is
	// surfaced as a banner and by marking the required fields.
	const missingContactPerson = typeof errors?.message === "string";

	const [selectedContactPerson, setSelectedContactPerson] = useState("");
	const [newContactPerson, setNewContactPerson] = useState<NewCustomerUser>(emptyContactPerson);

	const handleSelectCustomerUser = (id: string) => {
		setSelectedContactPerson(id);

		const { firstName, lastName, email, profile } = customerUsers.find((cu) => cu.id === id)!;

		const newContactPerson = {
			firstName,
			lastName,
			email,
			profile: { phoneNumber: profile?.phoneNumber || "" },
		};

		setNewContactPerson(newContactPerson);

		onChange && onChange(newContactPerson);
	};

	useEffect(() => {
		setNewContactPerson({
			firstName: value?.firstName || "",
			lastName: value?.lastName || "",
			email: value?.email || "",
			profile: {
				phoneNumber: value?.profile?.phoneNumber || "",
			},
		});

		setSelectedContactPerson(customerUsers.find((u) => u.email === value?.email)?.id || "");
	}, [value, customerUsers]);

	const handleContactPersonInputChange = (e: any) => {
		const contactPerson: NewCustomerUser = { ...newContactPerson };

		const value = e.target.value;

		switch (e.target.name) {
			case "firstName":
				contactPerson.firstName = value;
				break;
			case "lastName":
				contactPerson.lastName = value;
				break;
			case "phone":
				contactPerson.profile = { phoneNumber: value };
				break;
			case "email":
				contactPerson.email = value;
				break;
		}

		setNewContactPerson(contactPerson);

		onChange && onChange(contactPerson);
	};

	return (
		<div className={cn("flex flex-col gap-3", className)}>
			{/* Header row: field label left, picker button right — tight to the inputs
			    below. Fixed h-9 so sibling columns can match the vertical rhythm. */}
			<div className="flex h-9 items-center justify-between gap-3">
				<FormLabel margin="0" color={missingContactPerson ? "danger" : undefined}>
					{t("CONTACT_PERSON")} *
				</FormLabel>
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<OutlineButton
							label="Välj kontaktperson"
							icon={<ChevronDown size={15} />}
							disabled={!customerUsers.length}
						/>
					</DropdownMenuTrigger>
					<DropdownMenuContent align="end" className="max-h-72 w-64 overflow-y-auto">
						{customerUsers.map((user) => (
							<DropdownMenuItem key={user.id} onSelect={() => handleSelectCustomerUser(user.id)}>
								<Check
									size={15}
									className={cn(selectedContactPerson === user.id ? "opacity-100" : "opacity-0")}
								/>
								{user.firstName} {user.lastName}
							</DropdownMenuItem>
						))}
					</DropdownMenuContent>
				</DropdownMenu>
			</div>

			{missingContactPerson && (
				<div className="flex items-start gap-2 rounded-md border border-solid border-red-500 bg-red-50 px-3 py-2 text-sm text-red-700">
					<AlertCircle size={16} className="mt-0.5 shrink-0" />
					<span>{t(errors.message as any)}</span>
				</div>
			)}

			<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
				<AppInputNew
					value={newContactPerson?.firstName}
					onChange={(e: any) => handleContactPersonInputChange(e)}
					icon="name"
					name="firstName"
					placeholder="FIRST_NAME"
					// The array-level "no contact person" error has no field of its own,
					// so mark the one field that must be filled in — the banner above
					// carries the message.
					isValid={!missingContactPerson}
					errors={errors && errors[0]?.firstName?.message}
				/>
				<AppInputNew
					value={newContactPerson?.lastName}
					icon="name"
					onChange={(e: any) => handleContactPersonInputChange(e)}
					name="lastName"
					placeholder="LAST_NAME"
				/>
			</div>
			<AppInputNew
				value={newContactPerson?.profile?.phoneNumber}
				icon="phone"
				onChange={(e: any) => handleContactPersonInputChange(e)}
				name="phone"
				placeholder="PHONE"
			/>
			<AppInputNew
				value={newContactPerson?.email}
				icon="email"
				onChange={(e: any) => handleContactPersonInputChange(e)}
				name="email"
				placeholder="EMAIL"
				errors={errors && errors[0]?.email?.message}
			/>
		</div>
	);
};

export default ContactPerson;

interface ContactPersonControllerProps extends Omit<ContactPersonProps, "value" | "onChange"> {
	control: Control<any, any>;
	name: string;
	value?: CustomerUser[];
	onChange?: (users: CustomerUser[]) => void;
}

export const ContactPersonController: React.FC<ContactPersonControllerProps> = ({
	control,
	name,
	onChange,
	value,
	...rest
}) => {
	return (
		<Controller
			control={control}
			name={name}
			render={({ field }) => {
				const { ref, ...fieldWithoutRef } = field;

				return (
					<ContactPerson
						{...fieldWithoutRef}
						onChange={(e) => fieldWithoutRef.onChange([e])}
						value={fieldWithoutRef.value[0]}
						{...rest}
					/>
				);
			}}
		/>
	);
};
